import {
    ActionIcon,
    Badge,
    Button,
    Checkbox,
    Divider,
    Image,
    Input,
    Modal,
    Radio,
    Select,
    Text,
    TextInput
} from "@mantine/core";
import {
    IconCheck,
    IconChevronLeft,
    IconChevronRight,
    IconDeviceFloppy,
    IconPencil,
    IconPrinter,
} from "@tabler/icons-react";
import React, {
    ChangeEvent,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useSelector } from "react-redux";
import { getAllocationById } from "../../api/allocation";
import { getPono } from "../../api/poList";
import { saveForkliftData } from "../../api/saveFloorData";
import { getStyleList } from "../../api/styleList";
import NavBar from "../../components/NavBar/NavBar";
import PoTable from "../../components/PoTable/PoTable";
import PoLabel from "../../components/Print/PoLabel";
import PrintStoreLabels from "../../components/Print/PrintStorLabels";
import { useDebounce } from "../../Hooks/useDebounce";
import { PoNoWithStatus } from "../../interface/AllocationInvoice";
import { getImageBase64 } from "../../Utils/utils";
import "../Global.css";
import classes from "./Forklift.module.css";

type Direction = "next" | "prev";

type ItemType = "clothing" | "footwear";

type Orientation = "portrait" | "landscape";

interface PoState {
    poNo: string;
    poId: string;
}

interface PoTableRow {
    styleNo: string;
    // status: "Pending" | "Done";
    status: string;
    binLocation?: string | null;
}

export function formatStatus(status: string) {
    // replace "_" with space and capitalize first letter
    if (!status) return "";
    return status
        .replace(/_/g, " ")
        .replace(
            /\w\S*/g,
            (txt) =>
                txt.charAt(0).toUpperCase() +
                txt.substr(1).toLowerCase()
        );
}

function getUserNameFromEmail(email: string) {
    const parts = email.split("@");
    return parts[0] || "";
}

const Forklift: React.FC = () => {
    const [allocation, setAllocation] = useState<any>();
    const [allocationData, setAllocationData] = useState<any[]>([]);
    const [poNoList, setPoNoList] = useState<any[]>([]);
    const [po, setPo] = useState<PoState>({
        poNo: "",
        poId: "",
    });
    const [poTable, setPoTable] = useState<PoTableRow[]>([]);

    const [printModal, setPrintModal] = useState(false);
    const [viewModalOpened, setViewModalOpened] = useState(false);
    const [viewImageModal, setViewImageModal] = useState(false);

    const [dataAltered, setDataAltered] = useState(false);
    const [isEditingBin, setIsEditingBin] = useState(false);
    const [imageData, setImageData] = useState("");
    const [file, setFile] = useState<File>();

    const [sizeQty, setSizeQty] = useState("");
    const [itemType, setItemType] =
        useState<ItemType>("clothing");
    const [orientation, setOrientation] =
        useState<Orientation>("portrait");
    const [single, setSingle] = useState(false);
    const [boxQty, setBoxQty] = useState<number>();
    const [pickStartedAt, setPickStartedAt] = useState<string | null>(null);

    const token = useSelector(
        (state: any) => state.user.sessionId
    );

    const user = useSelector(
        (state: any) => state.user.user
    );

    const userName = useMemo(() => {
        if (!user) return "";
        return getUserNameFromEmail(user.email);
    }, [user]);


    const poSearch = useDebounce(po.poNo, 700);

    /* =====================================================
       DERIVED VALUES
    ===================================================== */

    const pendingCount = useMemo(
        () =>
            poTable.filter(
                (item) => item.status.toLowerCase() === "allocation" || item.status.toLowerCase() === "restock" || item.status.toLowerCase() === null || item.status.toLowerCase() === ""
            ).length,
        [poTable]
    );

    // check if it is single store or multiple stores
    const isSingleStore = useMemo(() => {
        return allocation?.storeName.length === 1;
    }, [allocation?.storeName]);

    // Count the number of done items
    const doneCount = useMemo(
        () =>
            poTable.length - pendingCount,
        [poTable]
    );

    const totalReceived = useMemo(() => {
        if (!allocation?.receivedQty) return 0;

        return allocation.receivedQty.reduce(
            (
                total: number,
                item: { quantity: number }
            ) => total + Number(item.quantity || 0),
            0
        );
    }, [allocation?.receivedQty]);
    const totalAllocation = useMemo(() => {
        if (!allocation?.totalAllocationPerSize) return 0;

        return allocation.totalAllocationPerSize.reduce(
            (
                total: number,
                quantity: number
            ) => total + Number(quantity || 0),
            0
        );
    }, [allocation?.totalAllocationPerSize]);

    const totalPicked = useMemo(() => {
        if (!allocation?.pickedQty) {
            return 0;
        }

        return allocation.pickedQty?.reduce(
            (total: number, item: { quantity: number }) =>
                total + Number(item.quantity || 0),
            0
        );
    }, [allocation?.pickedQty]);
    const handleBinLocationChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.currentTarget.value.toUpperCase();

        setAllocation((prev: any) => {
            if (!prev) return prev;

            return {
                ...prev,
                binlocation: value,
            };
        });

        setDataAltered(true);
    };
    const totalRemaining = useMemo(() => totalAllocation - totalPicked, [
        totalAllocation,
        totalPicked
    ]);

    const selectedStyleIndex = useMemo(
        () =>
            allocationData.findIndex(
                (item: any) =>
                    item.style_id === allocation?.style_id
            ),
        [allocationData, allocation?.style_id]
    );

    const canGoPrevious = selectedStyleIndex > 0;

    const canGoNext =
        selectedStyleIndex >= 0 &&
        selectedStyleIndex < allocationData.length - 1;

    /* =====================================================
       PO
    ===================================================== */

    const getPo = async () => {
        try {
            const poList = await getPono(
                token,
                po?.poNo
            );

            if (!poList) return;

            setPoNoList(
                poList?.data?.map(
                    (item: PoNoWithStatus) => ({
                        label: item.poNo,
                        value: item.poId!.toString(),
                    })
                ) || []
            );
        } catch (error) {
            console.error(
                "Unable to load PO list.",
                error
            );
        }
    };

    useEffect(() => {
        getPo();
    }, [poSearch]);

    const sortStyleByBinLocation = (
        styles: any[]
    ) => {
        const sorted = [...styles].sort(
            (a: any, b: any) => {
                const aLoc = a.binlocation;
                const bLoc = b.binlocation;

                if (!aLoc && !bLoc) return 0;
                if (!aLoc) return 1;
                if (!bLoc) return -1;

                const aMatch =
                    aLoc.match(/^([A-Z]+)(\d+)$/i);
                const bMatch =
                    bLoc.match(/^([A-Z]+)(\d+)$/i);

                if (!aMatch && !bMatch) return 0;
                if (!aMatch) return 1;
                if (!bMatch) return -1;

                const [, aLetters, aNumbers] =
                    aMatch;

                const [, bLetters, bNumbers] =
                    bMatch;

                if (
                    aLetters.length !==
                    bLetters.length
                ) {
                    return (
                        aLetters.length -
                        bLetters.length
                    );
                }

                const letterCompare =
                    aLetters.localeCompare(
                        bLetters,
                        undefined,
                        {
                            sensitivity: "base",
                        }
                    );

                if (letterCompare !== 0) {
                    return letterCompare;
                }

                return (
                    parseInt(aNumbers, 10) -
                    parseInt(bNumbers, 10)
                );
            }
        );

        return sorted.map(
            (item: any, index: number) => ({
                ...item,
                idx: index,
            })
        );
    };

    const setPoTableData = (data: any[]) => {
        const rows: PoTableRow[] =
            data?.map((item: any): PoTableRow => {

                const status: any = formatStatus(item.order_status);
                // item.allocation_status
                //     .slice(0, item.size_quantity_count)
                //     .some((value: boolean) => value === false)
                //     ? "Pending"
                //     : "Done";

                return {
                    styleNo: item.style_no,
                    status,
                    binLocation: item.binlocation,
                };
            }) || [];

        rows.sort((a, b) => {
            if (a.status === b.status) {
                return 0;
            }

            return a.status === "Pending" ? -1 : 1;
        });

        setPoTable(rows);
    };

    const getPickSessionKey = (
        allocationId: number
    ) => {
        return `forklift-pick-${userName}-${allocationId}`;
    };

    const startOrResumePickSession = (
        alloc: any
    ): string => {
        const key = getPickSessionKey(
            alloc.allocation_id
        );

        /*
         * If this allocation was already saved before,
         * the database start time is the source of truth.
         */
        if (alloc.pick_started_at) {
            setPickStartedAt(
                alloc.pick_started_at
            );

            return alloc.pick_started_at;
        }

        /*
         * Otherwise check if this browser already
         * started working on this style.
         */
        const existing =
            localStorage.getItem(key);

        if (existing) {
            try {
                const session =
                    JSON.parse(existing);

                if (session?.startedAt) {
                    setPickStartedAt(
                        session.startedAt
                    );

                    return session.startedAt;
                }
            } catch {
                localStorage.removeItem(key);
            }
        }

        /*
         * Completely new session.
         */
        const startedAt =
            new Date().toISOString();

        localStorage.setItem(
            key,
            JSON.stringify({
                allocationId:
                    alloc.allocation_id,

                startedAt,
            })
        );

        setPickStartedAt(startedAt);

        return startedAt;
    };

    const clearPickSession = (
        allocationId?: number
    ) => {
        if (!allocationId) {
            return;
        }

        localStorage.removeItem(
            getPickSessionKey(allocationId)
        );

        setPickStartedAt(null);
    };

    const loadAllocation = async (
        allocationId?: number
    ) => {
        if (!allocationId) return;

        const alloc = await getAllocationById(
            token,
            allocationId
        );

        /*
        * Start or resume the driver's timer.
        */
        startOrResumePickSession(alloc);

        setAllocation(alloc);
        setDataAltered(false);
        setIsEditingBin(false);

        if (alloc?.image) {
            const image =
                await getImageBase64(
                    `/uploads/${alloc.image}`
                );

            setImageData(image as string);
        } else {
            setImageData("");
        }
    };

    const handlePoNoChange = async (
        value: string | null
    ) => {
        if (allocation) {
            const saved =
                await handleSave(false);

            if (!saved) {
                return;
            }

            clearPickSession(
                allocation.allocation_id
            );
        }

        if (!value) {
            setPo({
                poId: "",
                poNo: "",
            });

            setAllocation(undefined);
            setAllocationData([]);
            setPoTable([]);

            return;
        }

        const selectedPo = poNoList.find(
            (item: any) =>
                item.value === value
        );

        if (!selectedPo) return;

        setPo({
            poId: value,
            poNo: selectedPo.label,
        });

        try {
            const styles = await getStyleList(
                token,
                parseInt(value, 10)
            );

            const sortedStyles =
                sortStyleByBinLocation(
                    styles || []
                );

            setAllocationData(sortedStyles);
            setPoTableData(sortedStyles);

            if (sortedStyles.length > 0) {
                await loadAllocation(
                    sortedStyles[0]
                        ?.allocation_id
                );
            } else {
                setAllocation(undefined);
            }
        } catch (error) {
            console.error(
                "Unable to load PO styles.",
                error
            );
        }
    };

    const refreshPoStyles = async (): Promise<any[]> => {
        if (!po.poId) {
            return [];
        }

        try {
            const styles = await getStyleList(
                token,
                Number(po.poId)
            );

            const sortedStyles =
                sortStyleByBinLocation(
                    styles || []
                );

            // Update navigation/source data
            setAllocationData(sortedStyles);

            // Update left-side PO table/statuses
            setPoTableData(sortedStyles);

            return sortedStyles;
        } catch (error) {
            console.error(
                "Unable to refresh style list.",
                error
            );

            return [];
        }
    };

    /* =====================================================
       STYLE NAVIGATION
    ===================================================== */

    const handleClick = async (
        styleNo: string
    ) => {

        if (
            styleNo.toLowerCase() ===
            allocation?.style_no?.toLowerCase()
        ) {
            return;
        }
        const saved = await handleSave(false);

        if (!saved) {
            return;
        }

        /*
         * Refresh the whole PO so the left table
         * reflects everybody's latest work.
         */
        const freshStyles =
            await refreshPoStyles();

        const selectedStyle =
            freshStyles.find(
                (item: any) =>
                    item.style_no === styleNo
            );

        if (!selectedStyle) {
            return;
        }
        clearPickSession(
            allocation?.allocation_id
        );

        await loadAllocation(
            selectedStyle.allocation_id
        );
    };

    const handleStyleChange = async (
        direction: Direction
    ) => {
        if (!allocation?.style_id) {
            return;
        }

        /*
         * Save current style first.
         *
         * This means our own status/qty changes reach
         * the DB before fetching the PO again.
         */
        const saved = await handleSave(false);

        if (!saved) {
            return;
        }

        /*
         * IMPORTANT:
         * Fetch the entire PO again.
         *
         * This picks up changes made by OTHER
         * forklift operators too.
         */
        const freshStyles =
            await refreshPoStyles();

        if (!freshStyles.length) {
            return;
        }

        /*
         * Find the currently selected style again
         * inside the fresh server response.
         */
        const currentIndex =
            freshStyles.findIndex(
                (item: any) =>
                    item.style_id ===
                    allocation.style_id
            );

        if (currentIndex < 0) {
            return;
        }

        let newIndex = currentIndex;

        if (
            direction === "next" &&
            currentIndex <
            freshStyles.length - 1
        ) {
            newIndex = currentIndex + 1;
        }

        if (
            direction === "prev" &&
            currentIndex > 0
        ) {
            newIndex = currentIndex - 1;
        }

        if (newIndex === currentIndex) {
            return;
        }

        const nextStyle =
            freshStyles[newIndex];

        clearPickSession(
            allocation?.allocation_id
        );

        await loadAllocation(
            nextStyle.allocation_id
        );
    };

    /* =====================================================
       QUANTITIES
    ===================================================== */

    const calculateOverstock = (
        receivedQty: any[],
        totalAllocationPerSize: number[]
    ) =>
        receivedQty.map(
            (item: any, index: number) =>
                Number(item.quantity || 0) -
                Number(
                    totalAllocationPerSize[
                    index
                    ] || 0
                )
        );

    const handleReceivedQtyChange = (
        e: ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const quantity = Number(
            e.target.value || 0
        );

        setAllocation((prev: any) => {
            if (!prev) return prev;

            const receivedQty =
                prev.receivedQty.map(
                    (
                        item: any,
                        currentIndex: number
                    ) =>
                        currentIndex === index
                            ? {
                                ...item,
                                quantity,
                            }
                            : item
                );

            const overstockPerSize =
                calculateOverstock(
                    receivedQty,
                    prev.totalAllocationPerSize
                );

            return {
                ...prev,
                receivedQty,
                total: receivedQty.reduce(
                    (
                        total: number,
                        item: any
                    ) =>
                        total +
                        Number(
                            item.quantity || 0
                        ),
                    0
                ),
                overstockPerSize,
            };
        });

        setDataAltered(true);
    };

    /*
     * Driver-facing Pick Qty.
     *
     * The existing backend stores quantities per store.
     * This screen shows one combined quantity per size.
     *
     * Any difference entered by the driver is applied
     * to the first store allocation so the existing
     * backend payload remains compatible.
     *
     * This strategy can later be extracted/configured
     * for your second picking screen.
     */
    const handlePickQtyChange = (
        e: ChangeEvent<HTMLInputElement>,
        sizeIndex: number
    ) => {
        const newTotal = Math.max(
            0,
            Number(e.target.value || 0)
        );

        setAllocation((prev: any) => {
            if (
                !prev ||
                !prev.sizeQuantities?.length
            ) {
                return prev;
            }

            const sizeQuantities =
                prev.sizeQuantities.map(
                    (store: any[]) =>
                        store.map(
                            (item: any) => ({
                                ...item,
                            })
                        )
                );

            const currentTotal =
                sizeQuantities.reduce(
                    (
                        total: number,
                        store: any[]
                    ) =>
                        total +
                        Number(
                            store[sizeIndex]
                                ?.quantity || 0
                        ),
                    0
                );

            const difference =
                newTotal - currentTotal;

            if (
                sizeQuantities[0]?.[
                sizeIndex
                ]
            ) {
                const currentFirstStoreQty =
                    Number(
                        sizeQuantities[0][
                            sizeIndex
                        ].quantity || 0
                    );

                sizeQuantities[0][
                    sizeIndex
                ].quantity = Math.max(
                    0,
                    currentFirstStoreQty +
                    difference
                );
            }

            const totalAllocationPerSize =
                prev.totalAllocationPerSize.map(
                    (
                        quantity: number,
                        index: number
                    ) =>
                        index === sizeIndex
                            ? newTotal
                            : quantity
                );

            const overstockPerSize =
                calculateOverstock(
                    prev.receivedQty,
                    totalAllocationPerSize
                );

            return {
                ...prev,
                sizeQuantities,
                totalAllocationPerSize,
                overstockPerSize,
            };
        });

        setDataAltered(true);
    };

    /* =====================================================
       INITIAL
    ===================================================== */

    const handleInitialChange = (
        value: string,
        storeIndex: number
    ) => {
        setAllocation((prev: any) => ({
            ...prev,

            initial: prev.initial.map(
                (
                    currentValue: string,
                    index: number
                ) =>
                    index === storeIndex
                        ? value
                        : currentValue
            ),
        }));

        setDataAltered(true);
    };

    const handleChecked = (
        e: ChangeEvent<HTMLInputElement>,
        storeIndex: number
    ) => {
        const first =
            user?.first_name?.[0]?.toUpperCase() ||
            "";

        const last =
            user?.last_name?.[0]?.toUpperCase() ||
            "";

        const initials = `${first}${last}`;

        handleInitialChange(
            e.currentTarget.checked
                ? initials
                : "SVP",
            storeIndex
        );
    };

    /* =====================================================
       STATUS
    ===================================================== */

    const handleStatusChange = (
        value: string
    ) => {
        setAllocation((prev: any) => ({
            ...prev,
            order_status: value,
        }));

        setDataAltered(true);
    };

    const handlePickedQtyChange = (
        e: ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const quantity = Math.max(
            0,
            Number(e.target.value || 0)
        );

        setAllocation((prev: any) => {
            if (!prev) return prev;

            const pickedQty = prev.pickedQty.map(
                (item: any, currentIndex: number) =>
                    currentIndex === index
                        ? {
                            ...item,
                            quantity,
                        }
                        : item
            );

            return {
                ...prev,
                pickedQty,
            };
        });

        setDataAltered(true);
    };
    /* =====================================================
       SAVE
    ===================================================== */

    const handleSave = async (
        showSuccessMessage = true,
        forceSave = false
    ) => {
        if (!dataAltered || !allocation) {
            return true;
        }
        if (!dataAltered && !forceSave) {
            return true;
        }


        if (
            allocation?.initial?.some(
                (item: string) =>
                    typeof item !== "string" ||
                    item.trim() === ""
            )
        ) {
            alert(
                "Please mention initial before saving."
            );

            return false;
        }
        // Check if remaining quantity is greater than 0 or greater than initial quantity
        if (
            allocation?.receivedQty?.some(
                (item: any) =>
                    item.quantity > 0 &&
                    item.quantity >
                    item.initialQuantity
            )
        ) {
            alert(
                "Remaining quantity cannot be greater than initial quantity."
            );

            return false;
        }

        try {

            const startedAt =
                pickStartedAt ||
                allocation.pick_started_at ||
                startOrResumePickSession(allocation);

            const saveData =
                // send sizeQuantities if it is single store
                await saveForkliftData(
                    token,
                    {
                        allocation_id: allocation.allocation_id,
                        style_id: allocation.style_id,
                        pickedQty: allocation.pickedQty,
                        order_status: allocation.order_status,
                        isSingleStore: isSingleStore,
                        binLocation: allocation.binlocation || "",
                        pickStartedAt: startedAt,
                        notes: allocation.notes || "",
                    }
                );
            //update the po table location
            if (!saveData) {
                alert(
                    "Could not save the data."
                );

                return false;
            }

            setPoTable((prev) =>
                prev.map((item) =>
                    item.styleNo ===
                        allocation.style_no
                        ? {
                            ...item,
                            binLocation: saveData.data.binlocation,
                        }
                        : item
                )
            );
            // await saveForkliftData(
            //     token,
            //     {
            //         ...allocation,

            //         initial:
            //             allocation.initial,

            //         allocation_status:
            //             allocation.initial.map(
            //                 (item: string) =>
            //                     item === "SVP"
            //                         ? false
            //                         : true
            //             ),
            //     }
            // );



            if (showSuccessMessage) {
                alert(
                    "Data saved successfully."
                );
            }

            if (saveData.data) {
                const newStatus = formatStatus(saveData.data.order_status);

                setAllocation((prev: any) => {
                    if (!prev) {
                        return prev;
                    }

                    return {
                        ...prev,

                        pick_started_at:
                            saveData.data
                                .pick_started_at ||
                            prev.pick_started_at ||
                            pickStartedAt,

                        pick_completed_at:
                            saveData.data
                                .pick_completed_at ||
                            prev.pick_completed_at,
                    };
                });

                setPoTable((prev) =>
                    prev
                        .map((item) =>
                            item.styleNo ===
                                saveData.data
                                    .style_no
                                ? {
                                    ...item,
                                    status: newStatus,
                                }
                                : item
                        )
                        .sort((a, b) => {
                            if (
                                a.status ===
                                b.status
                            ) {
                                return 0;
                            }

                            return a.status ===
                                "Pending"
                                ? -1
                                : 1;
                        })
                );

                setAllocationData(
                    (prev) =>
                        prev.map(
                            (item: any) =>
                                item.style_id ===
                                    saveData.data
                                        .style_id
                                    ? {
                                        ...item,

                                        sizeQuantities:
                                            saveData
                                                .data
                                                .sizeQuantities,

                                        description:
                                            saveData
                                                .data
                                                .description ||
                                            "",

                                        color:
                                            saveData
                                                .data
                                                .color,

                                        initial:
                                            saveData
                                                .data
                                                .initial,
                                    }
                                    : item
                        )
                );
            }
            if (saveData.data?.pick_started_at) {
                localStorage.removeItem(
                    getPickSessionKey(
                        allocation.allocation_id
                    )
                );

                setPickStartedAt(
                    saveData.data.pick_started_at
                );
            }
            setDataAltered(false);

            return true;
        } catch (error) {
            console.error(
                "Error saving data.",
                error
            );

            alert("Error saving data.");

            return false;
        }
    };

    /* =====================================================
       IMAGE / PRINT
    ===================================================== */

    const handleImageUrlChange = async (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const url = e.target.value;

        if (!url) return;

        try {
            const response = await fetch(url);
            const blob = await response.blob();

            const reader = new FileReader();

            reader.onload = () => {
                setImageData(
                    reader.result as string
                );

                setFile(
                    new File(
                        [blob],
                        "image.jpg",
                        {
                            type: blob.type,
                        }
                    )
                );
            };

            reader.readAsDataURL(blob);
        } catch (error) {
            console.error(
                "Unable to load image URL.",
                error
            );
        }
    };

    const handleFileChange = (
        selectedFile: File | null
    ) => {
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    /* =====================================================
       UNSAVED WARNING
    ===================================================== */

    useEffect(() => {
        const handleBeforeUnload = (
            event: BeforeUnloadEvent
        ) => {
            if (!dataAltered) return;

            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener(
            "beforeunload",
            handleBeforeUnload
        );

        return () => {
            window.removeEventListener(
                "beforeunload",
                handleBeforeUnload
            );
        };
    }, [dataAltered]);

    /* =====================================================
       PRINT PROPS
    ===================================================== */

    const printProps = allocation
        ? {
            allocationData: {
                storeName:
                    allocation.storeName || [],

                sizeQuantities:
                    allocation.sizeQuantities,
            },

            styleData: {
                color:
                    allocation?.color || "",

                cost:
                    allocation?.cost || 0,

                msrp:
                    allocation?.msrp || 0,

                description:
                    allocation?.description ||
                    "",

                supplier_name:
                    allocation?.supplierName ||
                    "",

                style_no:
                    allocation?.style_no ||
                    "",

                binlocation:
                    allocation?.binlocation ||
                    "",
                    
            },

            orientation,

            imageData,

            storeTotals:
                allocation?.sizeQuantities.map(
                    (store: any[]) =>
                        store.reduce(
                            (
                                total: number,
                                item: {
                                    quantity: number;
                                }
                            ) =>
                                total +
                                Number(
                                    item.quantity ||
                                    0
                                ),
                            0
                        )
                ),

            overstockTotal:
                allocation?.overstockPerSize.reduce(
                    (
                        total: number,
                        quantity: number
                    ) =>
                        total +
                        Number(
                            quantity || 0
                        ),
                    0
                ),

            styleNo:
                allocation.style_no || "",

            itemType,

            single,

            totalAllocation:
                allocation.totalAllocationPerSize.reduce(
                    (
                        total: number,
                        quantity: number
                    ) =>
                        total +
                        Number(
                            quantity || 0
                        ),
                    0
                ),

            file,

            manualsizeQty: sizeQty,
        }
        : undefined;

    /* =====================================================
       UI
    ===================================================== */

    return (
        <div className={classes.page}>
            <NavBar />

            <main className={classes.main}>
                {/* PAGE HEADER */}
                <header
                    className={classes.pageHeader}
                >
                    <div>
                        <Text
                            className={
                                classes.pageTitle
                            }
                        >
                            Pick orders
                        </Text>

                        <Text
                            className={
                                classes.pageSubtitle
                            }
                        >
                            Select a PO, choose a
                            style and start picking
                        </Text>
                    </div>

                    <div
                        className={
                            classes.poSearch
                        }
                    >
                        <Text
                            size="sm"
                            fw={700}
                            mb={5}
                        >
                            Purchase Order
                        </Text>

                        <Select
                            data={poNoList}
                            placeholder="Search or select PO"
                            searchable
                            clearable
                            size="md"
                            value={
                                po.poId || null
                            }
                            onChange={
                                handlePoNoChange
                            }
                            onSearchChange={(
                                value
                            ) =>
                                setPo((prev) => ({
                                    ...prev,
                                    poNo: value.toUpperCase(),
                                }))
                            }
                        />
                    </div>
                </header>

                {!allocation && (
                    <section
                        className={
                            classes.emptyState
                        }
                    >
                        <Text
                            fw={700}
                            size="lg"
                        >
                            Select a purchase order
                        </Text>

                        <Text
                            size="sm"
                            c="dimmed"
                        >
                            Styles and picking
                            information will appear
                            here.
                        </Text>
                    </section>
                )}

                {allocation && (
                    <>
                        <section
                            className={
                                classes.workspace
                            }
                        >
                            {/* STYLE LIST */}
                            <aside
                                className={
                                    classes.stylePanel
                                }
                            >
                                <div
                                    className={
                                        classes.panelHeader
                                    }
                                >
                                    <div>
                                        <Text
                                            fw={700}
                                            size="lg"
                                        >
                                            Styles in this PO
                                        </Text>

                                        <Text
                                            size="xs"
                                            c="dimmed"
                                        >
                                            Tap a style
                                            to start
                                            picking
                                        </Text>
                                    </div>

                                    <Badge
                                        variant="light"
                                        color="gray"
                                        size="lg"
                                    >
                                        {
                                            poTable.length
                                        }{" "}
                                        Styles
                                    </Badge>
                                </div>

                                <div
                                    className={classes.styleStats}
                                >
                                    <div
                                        className={classes.stat}
                                    >
                                        <Text
                                            size="xs"
                                            c="dimmed"
                                        >
                                            Pending
                                        </Text>

                                        <Text
                                            fw={700}
                                            size="lg"
                                        >
                                            {pendingCount}
                                        </Text>
                                    </div>

                                    <div
                                        className={classes.stat}
                                    >
                                        <Text
                                            size="xs"
                                            c="dimmed"
                                        >
                                            Done
                                        </Text>

                                        <Text
                                            fw={700}
                                            size="lg"
                                        >
                                            {doneCount}
                                        </Text>
                                    </div>
                                </div>

                                <div
                                    className={
                                        classes.poTable
                                    }
                                >
                                    <PoTable
                                        data={poTable}
                                        selectedStyle={
                                            allocation?.style_no
                                        }
                                        handleClick={
                                            handleClick
                                        }
                                    />
                                </div>
                            </aside>

                            {/* PICKING PANEL */}
                            <section
                                className={
                                    classes.pickPanel
                                }
                            >
                                <div className={classes.styleDetails}>
                                    <div className={classes.styleInfoArea}>
                                        <div className={classes.styleNumberSection}>
                                            <Text className={classes.fieldLabel}>
                                                Style No
                                            </Text>

                                            <button
                                                type="button"
                                                className={classes.styleNumber}
                                                onClick={() =>
                                                    setViewModalOpened(true)
                                                }
                                            >
                                                {allocation.style_no}
                                            </button>
                                        </div>

                                        <div className={classes.styleSecondaryInfo}>
                                            <div>
                                                <Text className={classes.fieldLabel}>
                                                    Color
                                                </Text>

                                                <Text className={classes.fieldValue}>
                                                    {allocation.color || "N/A"}
                                                </Text>
                                            </div>

                                            <div>
                                                <Text className={classes.fieldLabel}>
                                                    Description
                                                </Text>

                                                <Text className={classes.fieldValue}>
                                                    {allocation.description || "N/A"}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={classes.binCard}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                            <div></div>
                                            <Text className={classes.binLabel}>
                                                Bin Location
                                            </Text>

                                            <ActionIcon
                                                variant="subtle"
                                                size="sm"
                                                onClick={() =>
                                                    setIsEditingBin((prev) => !prev)
                                                }
                                                aria-label={
                                                    isEditingBin
                                                        ? "Finish editing bin location"
                                                        : "Edit bin location"
                                                }
                                            >
                                                {isEditingBin ? (
                                                    <IconCheck size={18} />
                                                ) : (
                                                    <IconPencil size={18} />
                                                )}
                                            </ActionIcon>
                                        </div>

                                        {isEditingBin ? (
                                            <TextInput
                                                value={allocation.binlocation || ""}
                                                placeholder="Enter bin location"
                                                autoFocus
                                                size="sm"
                                                onChange={handleBinLocationChange}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        setIsEditingBin(false);
                                                    }

                                                    if (e.key === "Escape") {
                                                        setIsEditingBin(false);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Text className={classes.binValue}>
                                                {allocation.binlocation || "N/A"}
                                            </Text>
                                        )}
                                    </div>
                                </div>

                                <Divider />

                                {/* SIZE QUANTITIES */}
                                <div
                                    className={
                                        classes.quantitySection
                                    }
                                >
                                    {/* STATUS */}
                                    <div
                                        className={
                                            classes.sectionHeading
                                        }
                                    >
                                    </div>
                                    <div>
                                        <Text
                                            fw={700}
                                            size="sm"
                                            mb={8}
                                        >
                                            Picking Status
                                        </Text>

                                        <Radio.Group
                                            value={
                                                allocation?.order_status ||
                                                "restock"
                                            }
                                            onChange={
                                                handleStatusChange
                                            }
                                        >
                                            <div
                                                className={
                                                    classes.statusGrid
                                                }
                                            >
                                                <Radio
                                                    value="restock"
                                                    label="Pending"
                                                    size="md"
                                                    className={
                                                        classes.statusOption
                                                    }
                                                />

                                                <Radio
                                                    value="picked"
                                                    label="Picked"
                                                    size="md"
                                                    className={
                                                        classes.statusOption
                                                    }
                                                />

                                                <Radio
                                                    value="not_found"
                                                    label="Not Found"
                                                    size="md"
                                                    className={
                                                        classes.statusOption
                                                    }
                                                />

                                                <Radio
                                                    value="picked_all"
                                                    label="Picked All"
                                                    size="md"
                                                    className={
                                                        classes.statusOption
                                                    }
                                                />
                                            </div>
                                        </Radio.Group>
                                    </div>

                                    <div
                                        className={
                                            classes.quantityTable
                                        }
                                        style={{ overflow: "auto", background: "white" }}
                                    >
                                        <div
                                            className={
                                                classes.quantityHeader
                                            }
                                        >
                                            <span>
                                                Size
                                            </span>

                                            <span>
                                                Received Qty
                                            </span>

                                            <span>
                                                Allocated Qty
                                            </span>
                                            <span>
                                                Pick Qty
                                            </span>

                                            <span>
                                                Remaining
                                            </span>
                                        </div>

                                        {allocation.receivedQty.map(
                                            (
                                                item: any,
                                                index: number
                                            ) => {
                                                const size =
                                                    allocation
                                                        ?.sizeQuantities?.[0]?.[
                                                        index
                                                    ]
                                                        ?.size ||
                                                    "-";
                                                const receivedQty =
                                                    Number(allocation.receivedQty[index]?.quantity || 0);

                                                const allocatedQty =
                                                    Number(allocation.totalAllocationPerSize[index] || 0);

                                                const pickedQty =
                                                    Number(allocation.pickedQty[index]?.quantity || 0);

                                                const remainingToPick =
                                                    allocatedQty - pickedQty;

                                                return (
                                                    <div
                                                        className={classes.quantityRow}
                                                        key={`${size}-${index}`}
                                                        style={{
                                                            backgroundColor:
                                                                remainingToPick === 0
                                                                    ? "#238e0b24"
                                                                    : "transparent",

                                                        }}
                                                    >
                                                        {/* SIZE */}
                                                        <Text
                                                            fw={700}
                                                            className={classes.sizeValue}
                                                        >
                                                            {size}
                                                        </Text>

                                                        {/* RECEIVED QTY - READ ONLY */}
                                                        <Text
                                                            fw={600}
                                                            className={classes.quantityValue}
                                                        >
                                                            {receivedQty}
                                                        </Text>

                                                        {/* ALLOCATED QTY - READ ONLY */}
                                                        <Text
                                                            fw={600}
                                                            className={classes.quantityValue}
                                                        >
                                                            {allocatedQty}
                                                        </Text>

                                                        {/* PICKED QTY - EDITABLE */}
                                                        <TextInput
                                                            type="number"
                                                            inputMode="numeric"
                                                            min={0}
                                                            max={allocatedQty}
                                                            value={pickedQty}
                                                            className={classes.quantityInput}
                                                            onChange={(e) =>
                                                                handlePickedQtyChange(e, index)
                                                            }
                                                        />

                                                        {/* REMAINING TO PICK */}
                                                        <Text
                                                            fw={700}
                                                            className={
                                                                remainingToPick < 0
                                                                    ? classes.negativeRemaining
                                                                    : classes.remainingValue
                                                            }
                                                        >
                                                            {remainingToPick}
                                                        </Text>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>

                                    <div
                                        className={
                                            classes.totalGrid
                                        }
                                    >
                                        <div
                                            className={
                                                classes.totalCard
                                            }
                                        >
                                            <Text
                                                className={
                                                    classes.totalLabel
                                                }
                                            >
                                                Total
                                                Received
                                            </Text>

                                            <Text
                                                className={
                                                    classes.totalValue
                                                }
                                            >
                                                {
                                                    totalReceived
                                                }
                                            </Text>
                                        </div>

                                        <div
                                            className={
                                                classes.totalCard
                                            }
                                        >
                                            <Text
                                                className={
                                                    classes.totalLabel
                                                }
                                            >
                                                Total to
                                                Pick
                                            </Text>

                                            <Text
                                                className={
                                                    classes.totalValue
                                                }
                                            >
                                                {
                                                    totalPicked
                                                }
                                            </Text>
                                        </div>

                                        <div
                                            className={
                                                classes.totalCard
                                            }
                                        >
                                            <Text
                                                className={
                                                    classes.totalLabel
                                                }
                                            >
                                                Remaining
                                            </Text>

                                            <Text
                                                className={
                                                    totalRemaining <
                                                        0
                                                        ? classes.totalValueNegative
                                                        : classes.totalValue
                                                }
                                            >
                                                {
                                                    totalRemaining
                                                }
                                            </Text>
                                        </div>
                                    </div>
                                </div>

                                <Divider />

                                {/* NOTE */}
                                <TextInput
                                    label="Note"
                                    placeholder="Add a note (optional)"
                                    size="md"
                                    value={
                                        allocation.notes ||
                                        ""
                                    }
                                    onChange={(e) => {
                                        setAllocation(
                                            (
                                                prev: any
                                            ) => ({
                                                ...prev,
                                                notes: e
                                                    .target
                                                    .value,
                                            })
                                        );

                                        setDataAltered(
                                            true
                                        );
                                    }}
                                />

                                {/* INTERNAL INITIALS */}
                                {false && allocation
                                    ?.storeName
                                    ?.length >
                                    0 && (
                                        <div
                                            className={
                                                classes.initialSection
                                            }
                                        >
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                                mb={6}
                                            >
                                                Pick
                                                confirmation
                                            </Text>

                                            <div
                                                className={
                                                    classes.initialList
                                                }
                                            >
                                                {allocation.storeName.map(
                                                    (
                                                        store: string,
                                                        storeIndex: number
                                                    ) => (
                                                        <label
                                                            className={
                                                                classes.initialItem
                                                            }
                                                            key={`${store}-${storeIndex}`}
                                                        >
                                                            <Checkbox
                                                                checked={
                                                                    allocation
                                                                        .initial[
                                                                    storeIndex
                                                                    ] !==
                                                                    "SVP" &&
                                                                    allocation
                                                                        .initial[
                                                                    storeIndex
                                                                    ] !==
                                                                    ""
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    handleChecked(
                                                                        e,
                                                                        storeIndex
                                                                    )
                                                                }
                                                            />

                                                            <Text
                                                                size="sm"
                                                                fw={
                                                                    600
                                                                }
                                                            >
                                                                {
                                                                    store
                                                                }
                                                            </Text>
                                                        </label>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}

                                {/* NAVIGATION */}
                                <div
                                    className={
                                        classes.navigation
                                    }
                                >
                                    <Button
                                        variant="default"
                                        size="md"
                                        leftSection={
                                            <IconChevronLeft
                                                size={
                                                    18
                                                }
                                            />
                                        }
                                        disabled={
                                            !canGoPrevious
                                        }
                                        onClick={() =>
                                            handleStyleChange(
                                                "prev"
                                            )
                                        }
                                    >
                                        Previous
                                        Style
                                    </Button>

                                    <Button
                                        size="md"
                                        rightSection={
                                            <IconChevronRight
                                                size={
                                                    18
                                                }
                                            />
                                        }
                                        disabled={
                                            !canGoNext
                                        }
                                        onClick={() =>
                                            handleStyleChange(
                                                "next"
                                            )
                                        }
                                    >
                                        Next Style
                                    </Button>
                                </div>
                            </section>
                        </section>
                    </>
                )}
            </main>

            {/* STICKY ACTION BAR */}
            <footer className={classes.footer}>
                <div
                    className={
                        classes.footerInner
                    }
                >
                    <Button
                        variant="default"
                        size="md"
                        leftSection={
                            <IconPrinter size={20} />
                        }
                        disabled={!allocation}
                        onClick={() =>
                            setPrintModal(true)
                        }
                    >
                        Print Labels
                    </Button>

                    <Button
                        size="md"
                        leftSection={
                            <IconDeviceFloppy
                                size={20}
                            />
                        }
                        disabled={
                            !allocation ||
                            !dataAltered
                        }
                        onClick={() =>
                            handleSave(true, true)
                        }
                    >
                        {dataAltered
                            ? "Save Changes"
                            : "Saved"}
                    </Button>
                </div>
            </footer>

            {/* STYLE IMAGE MODAL */}
            <Modal
                opened={viewModalOpened}
                onClose={() =>
                    setViewModalOpened(false)
                }
                title={allocation?.style_no}
                size="xl"
                centered
            >
                <div
                    className={
                        classes.viewModal
                    }
                >
                    <Image
                        src={`/styleImages/${allocation?.image}`}
                        alt={
                            allocation?.style_no ||
                            "Style image"
                        }
                        fit="contain"
                    />
                </div>
            </Modal>

            {/* PRINT MODAL */}
            <Modal
                opened={printModal}
                onClose={() =>
                    setPrintModal(false)
                }
                title="Print Label Options"
                centered
                size="xl"
            >
                <div
                    className={
                        classes.printOptions
                    }
                >
                    <div
                        className={
                            classes.printGrid
                        }
                    >
                        <TextInput
                            label="Size / Qty"
                            value={sizeQty}
                            onChange={(e) =>
                                setSizeQty(
                                    e.target.value.toUpperCase()
                                )
                            }
                        />

                        <Select
                            label="Orientation"
                            data={[
                                "portrait",
                                "landscape",
                            ]}
                            value={orientation}
                            disabled={single}
                            onChange={(value) =>
                                setOrientation(
                                    (value ||
                                        "portrait") as Orientation
                                )
                            }
                        />

                        <div>
                            <Text
                                fw={500}
                                size="sm"
                                mb={5}
                            >
                                Image
                            </Text>

                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const selectedFile =
                                        e.target
                                            .files?.[0];

                                    if (
                                        !selectedFile
                                    ) {
                                        return;
                                    }

                                    const reader =
                                        new FileReader();

                                    reader.onload =
                                        () => {
                                            if (
                                                !reader.result
                                            ) {
                                                return;
                                            }

                                            setImageData(
                                                reader.result.toString()
                                            );

                                            handleFileChange(
                                                selectedFile
                                            );
                                        };

                                    reader.readAsDataURL(
                                        selectedFile
                                    );
                                }}
                            />
                        </div>

                        <TextInput
                            label="Image URL"
                            onChange={
                                handleImageUrlChange
                            }
                        />

                        <Select
                            label="Type"
                            data={[
                                {
                                    label: "Clothing",
                                    value: "clothing",
                                },
                                {
                                    label: "Footwear / Accessories",
                                    value: "footwear",
                                },
                            ]}
                            value={itemType}
                            onChange={(value) =>
                                setItemType(
                                    (value ||
                                        "clothing") as ItemType
                                )
                            }
                        />

                        <div
                            className={
                                classes.checkboxField
                            }
                        >
                            <Text
                                fw={500}
                                size="sm"
                            >
                                Single Page
                            </Text>

                            <Checkbox
                                checked={single}
                                onChange={(e) => {
                                    setSingle(
                                        e.target.checked
                                    );

                                    if (
                                        e.target.checked
                                    ) {
                                        setOrientation(
                                            "portrait"
                                        );
                                    }
                                }}
                            />
                        </div>

                        <TextInput
                            label="Packed Qty per Box"
                            placeholder="Quantity per box"
                            type="number"
                            value={
                                boxQty ?? ""
                            }
                            onChange={(e) =>
                                setBoxQty(
                                    e.target.value
                                        ? Number(
                                            e.target
                                                .value
                                        )
                                        : undefined
                                )
                            }
                        />
                    </div>

                    {allocation &&
                        printProps && (
                            <div
                                className={
                                    classes.printActions
                                }
                            >
                                <PrintStoreLabels
                                    props={
                                        printProps
                                    }
                                />

                                <PrintStoreLabels
                                    props={{
                                        ...printProps,
                                        boxQty,
                                        btnLabel:
                                            "Store Label",
                                    }}
                                />

                                {allocation?.image && (
                                    <Button
                                        variant="default"
                                        onClick={() =>
                                            setViewImageModal(
                                                true
                                            )
                                        }
                                    >
                                        View Image
                                    </Button>
                                )}
                                {po?.poNo && (<PoLabel
                                    poNo={po?.poNo}
                                />)}
                            </div>
                        )}
                </div>
            </Modal>

            {/* IMAGE PREVIEW MODAL */}
            <Modal
                opened={viewImageModal}
                onClose={() =>
                    setViewImageModal(false)
                }
                title="Image Preview"
                centered
                size="lg"
            >
                <Image
                    src={`/uploads/${allocation?.image}`}
                    fit="contain"
                />
            </Modal>
        </div>
    );
};

export default Forklift;
